import { useAuthStore } from '~/stores/useAuthStore'
import { useGameStore } from '~/stores/useGameStore'
import { usePlayerStore } from '~/stores/usePlayerStore'
import { OFFLINE_MIN_ABSENCE_SEC } from '~/utils/offlineSession'
import {
  beaconDaycareSyncOnPageHide,
  flushDaycareBeforeOfflineDepart,
  persistDaycareLocalBackup,
} from '~/composables/useDaycareSync'
import { isGameTabLeader } from '~/composables/useMultiTabGuard'
import { useModalQueue } from '~/composables/useModalQueue'
import { useOfflineReportModal } from '~/composables/useOfflineReportModal'

export { OFFLINE_MIN_ABSENCE_SEC }

/** Évite un double envoi (logout explicite + pagehide/beforeunload). */
let offlineDepartBeaconSent = false
/** Ne pas ancrer l'offline quand la session est invalidée (ex. autre appareil). */
let offlineDepartBeaconSuppressed = false

/** Instant (ms) où l'onglet est passé en arrière-plan — sert à mesurer l'absence au retour. */
let backgroundHiddenAtMs = 0
/** Dernier checkpoint d'ancrage offline — anti-rafale sur les alt-tab rapprochés. */
let lastBackgroundAnchorCheckpointMs = 0
const BACKGROUND_ANCHOR_CHECKPOINT_MIN_INTERVAL_MS = 10_000

export function suppressOfflineDepartBeacon(): void {
  offlineDepartBeaconSuppressed = true
}

export function markOfflineDepartBeaconSent(): void {
  offlineDepartBeaconSent = true
}

export function resetOfflineDepartBeaconState(): void {
  offlineDepartBeaconSent = false
  offlineDepartBeaconSuppressed = false
  backgroundHiddenAtMs = 0
}

function buildOfflineDepartPayload(sessionToken: string) {
  const auth = useAuthStore()
  const game = useGameStore()
  const player = usePlayerStore()
  game.saveOfflineSnapshot()
  return {
    snapshotDps: game.snapshotDps,
    snapshotZoneDps: game.snapshotZoneDps,
    snapshotGoldRate: game.snapshotGoldRate,
    snapshotEnemyMaxHp: game.snapshotEnemyMaxHp,
    snapshotGoldReward: game.snapshotGoldReward,
    ...(player.combatGeneration != null &&
    player.combatZone != null &&
    !player.isFrontierReturnGuarded()
      ? {
          combatGeneration: player.combatGeneration,
          combatZone: player.combatZone,
        }
      : {}),
    adminVersion: player.adminVersion,
    sessionToken,
    serverBootId: auth.serverBootId,
  }
}

/**
 * Offline logout → login : enregistre le départ serveur avant auth logout.
 */
export function useOfflinePresence() {
  const auth = useAuthStore()
  const api = useApi()

  async function recordDepartureForLogout(): Promise<void> {
    if (!auth.isAuthenticated || !auth.gameLoaded) return
    await auth.resyncSessionTokenIfMissing()
    if (!auth.sessionToken) return

    await flushDaycareBeforeOfflineDepart()
    try {
      await api.post('/api/game/offline-depart', buildOfflineDepartPayload(auth.sessionToken))
      markOfflineDepartBeaconSent()
    } catch (e: unknown) {
      console.warn('[OFFLINE-DEPART] Failed:', e)
    }
  }

  return {
    recordDepartureForLogout,
    OFFLINE_MIN_ABSENCE_SEC,
  }
}

/**
 * Onglet passé en arrière-plan (onglet « qui dort sur le côté », bascule d'app ou écran
 * verrouillé sur mobile). Fige un checkpoint d'ancrage en `keepalive` : le serveur enregistre
 * `_lastSaveAtSec` = maintenant + un snapshot frais. L'absence offline démarre donc exactement
 * ici, sans fermeture ni déconnexion. Le `keepalive` survit au gel/déchargement de l'onglet
 * (essentiel sur mobile, où un `setTimeout` différé ne se déclencherait jamais).
 */
export function anchorOfflineOnHidden(): void {
  if (typeof document === 'undefined') return
  const auth = useAuthStore()
  if (!auth.isAuthenticated || !auth.gameLoaded) return
  // Seul l'onglet leader ancre / sauvegarde (cohérent avec le reste du jeu).
  if (!isGameTabLeader()) return

  const nowMs = Date.now()
  backgroundHiddenAtMs = nowMs
  // Anti-rafale : un seul checkpoint d'ancrage par fenêtre de 10 s (alt-tab rapprochés).
  if (nowMs - lastBackgroundAnchorCheckpointMs < BACKGROUND_ANCHOR_CHECKPOINT_MIN_INTERVAL_MS) {
    return
  }
  lastBackgroundAnchorCheckpointMs = nowMs
  // Checkpoint léger en keepalive : fige snapshot + `_lastSaveAtSec` côté serveur.
  void auth.saveGameCheckpoint(true)
}

/**
 * Onglet revenu au premier plan : si l'absence a dépassé le seuil offline (5 min), règle
 * l'absence côté serveur (filet `estimated`, reconstruit depuis le checkpoint d'ancrage) et
 * présente le rapport « Pendant ton absence… ». No-op pour un simple aller-retour rapide,
 * ce qui évite un calcul offline (coûteux) à chaque bascule d'onglet.
 */
export async function settleOfflineOnVisible(): Promise<void> {
  const hiddenAtMs = backgroundHiddenAtMs
  backgroundHiddenAtMs = 0
  if (hiddenAtMs <= 0) return
  if (Date.now() - hiddenAtMs < OFFLINE_MIN_ABSENCE_SEC * 1000) return

  const auth = useAuthStore()
  if (!auth.isAuthenticated || !auth.gameLoaded) return

  // Règle l'absence : GET offline-report applique le rapport serveur et met offlineStatus à jour.
  await auth.hydrateOfflineReportInBackground()
  presentOfflineReturnReportIfSignificant()
}

/** Ouvre le modal de rapport offline au retour, en respectant la file des modales. */
function presentOfflineReturnReportIfSignificant(): void {
  const game = useGameStore()
  if (game.offlineStatus !== 'claimable') return
  const away =
    game.activeOfflineReport?.awaySeconds ?? game.offlineHistory[0]?.awaySeconds ?? null
  if (away == null || away < OFFLINE_MIN_ABSENCE_SEC) return
  const modalQueue = useModalQueue()
  const { openOfflineReportModal } = useOfflineReportModal()
  modalQueue.requestModal('offline', 2)
  if (modalQueue.canShowModal('offline')) void openOfflineReportModal()
}

/**
 * Fermeture d'onglet / navigation : POST keepalive fire-and-forget vers offline-depart.
 * Même principe que `beaconFlushCombatGoldCredit` — survit à la fermeture de page.
 * Ne s'exécute pas en bfcache (`event.persisted`) ni si un départ a déjà été enregistré.
 */
export function beaconOfflineDepartOnPageHide(event?: PageTransitionEvent): void {
  if (event?.persisted) return
  if (offlineDepartBeaconSent || offlineDepartBeaconSuppressed) return

  const auth = useAuthStore()
  if (!auth.isAuthenticated || !auth.gameLoaded || !auth.sessionToken) return
  if (!isGameTabLeader()) return

  offlineDepartBeaconSent = true
  persistDaycareLocalBackup()
  // Pension d'abord (état œufs) puis ancrage offline-depart.
  beaconDaycareSyncOnPageHide()

  try {
    void useApi()
      .post('/api/game/offline-depart', buildOfflineDepartPayload(auth.sessionToken), {
        keepalive: true,
      })
      .catch(() => {
        // Discret : ne jamais bloquer la fermeture de page.
      })
  } catch {
    /* garde-fou synchrone */
  }
}
